import React, { useEffect, useState } from "react";
import { Button, Spinner } from "reactstrap";

import api from "../../services/api";
import { useCompanies } from "../../hooks/companies";
import { AUTOPILOT_COLORS } from "../autopilot/styles";

// PO1-3197: "Calcular frete" / "Recalcular frete" no detalhe do CT-e.
//
// Usa os mesmos endpoints da tela Recalcular Frete, filtrando pela chave do CT-e.
// O cálculo por CT-e só olha, por padrão, os últimos 30 dias de emissão; por
// isso a janela vai de um dia antes a um dia depois da emissão deste CT-e.
// Antes de calcular, a prévia diz se ele é elegível: quando não é (ex.: fatura
// liberada/paga), mostra o motivo que o próprio cálculo devolve em `diagnostico`.
//
// PO1-3367: `bloqueio` (motivo vindo do detalhe: CT-e em fatura Validada,
// Liberada ou Paga) deixa o botão desabilitado com o motivo à vista, sem
// precisar clicar para descobrir.

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function diaIso(base, deltaDias) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(base || ""));
  if (!m) return undefined;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + deltaDias));
  return d.toISOString().slice(0, 10);
}

function mensagemDeErro(e, padrao) {
  return (
    (e && e.response && e.response.data && (e.response.data.error || e.response.data.message)) ||
    (e && e.message) ||
    padrao
  );
}

const MSG_BLOQUEADO =
  "Recálculo bloqueado: a NF, o CT-e ou a fatura está travada para recálculo (ex.: fatura já liberada).";

const TONS = {
  ok: AUTOPILOT_COLORS.statusSuccess,
  aviso: AUTOPILOT_COLORS.statusWarning,
  erro: AUTOPILOT_COLORS.statusDanger,
};

export default function RecalcularFreteCteButton({ summary, simulado, bloqueio, onRecalculated }) {
  const { empresa } = useCompanies();
  const embarcadorId = empresa && empresa.value;
  const chave = summary && summary.chaveAcesso;
  const temSimulado = Number(simulado) > 0;

  const [executando, setExecutando] = useState(false);
  const [resultado, setResultado] = useState(null); // { tom, texto }

  // Outro CT-e no mesmo modal: começa sem mensagem.
  useEffect(() => {
    setResultado(null);
    setExecutando(false);
  }, [chave]);

  if (!chave || !embarcadorId) return null;

  const calcular = async () => {
    if (executando) return;
    setExecutando(true);
    setResultado(null);
    const body = {
      embarcadorId,
      chaveCTe: chave,
      dataEmissaoCTeDe: diaIso(summary.dataEmissao, -1),
      dataEmissaoCTeAte: diaIso(summary.dataEmissao, 1),
    };
    try {
      const previa = await api.post("/recalculo/cte/preview", body);
      const total = Number(previa && previa.data && previa.data.total);
      if (!total) {
        const diag = previa && previa.data && previa.data.diagnostico;
        setResultado({
          tom: "aviso",
          texto: (diag && diag.mensagem) || "Este CT-e não é elegível para cálculo agora.",
        });
        return;
      }

      const resposta = await api.post("/recalculo/cte", body);
      const itens = (resposta && resposta.data && resposta.data.resultados) || [];
      const item = itens[0];
      if (!item) {
        setResultado({ tom: "aviso", texto: "O cálculo não retornou resultado para este CT-e." });
        return;
      }
      if (!item.sucesso) {
        // "Sem dados elegíveis" é o retorno do cálculo quando a NF, o CT-e ou a
        // fatura está travada para recálculo (validarBloqueio do calc-frete-service);
        // a prévia não enxerga essa trava, só a situação do vínculo com a fatura.
        const bloqueado = /sem dados eleg/i.test(String(item.erro || ""));
        setResultado({
          tom: bloqueado ? "aviso" : "erro",
          texto: bloqueado
            ? MSG_BLOQUEADO
            : item.erro || "Não foi possível calcular o frete deste CT-e.",
        });
        return;
      }
      const novo = item.totais && item.totais.freteComImposto;
      setResultado({
        tom: "ok",
        texto:
          Number(novo) > 0
            ? `Frete ${temSimulado ? "recalculado" : "calculado"}: ${formatCurrency(novo)}`
            : "Cálculo concluído, mas o simulado ficou zerado (sem tabela ou regra aplicável).",
      });
      if (onRecalculated) await onRecalculated();
    } catch (e) {
      const texto = mensagemDeErro(e, "Erro ao calcular o frete.");
      setResultado(
        /sem dados eleg/i.test(String(texto))
          ? { tom: "aviso", texto: MSG_BLOQUEADO }
          : { tom: "erro", texto }
      );
    } finally {
      setExecutando(false);
    }
  };

  return (
    <div
      className="cte-audit-detail-recalculo"
      style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "0.35rem", maxWidth: 360 }}
    >
      <Button
        size="sm"
        color="info"
        outline={temSimulado}
        onClick={calcular}
        disabled={executando || Boolean(bloqueio)}
        title={bloqueio || undefined}
        style={{ whiteSpace: "nowrap", marginTop: "0.2rem" }}
      >
        {executando ? (
          <>
            <Spinner size="sm" /> Calculando…
          </>
        ) : temSimulado ? (
          "Recalcular frete"
        ) : (
          "Calcular frete"
        )}
      </Button>
      {bloqueio && !resultado ? (
        <div style={{ fontSize: "0.78rem", lineHeight: 1.35, textAlign: "right", color: AUTOPILOT_COLORS.muted }}>
          {bloqueio}
        </div>
      ) : null}
      {resultado ? (
        <div
          role="status"
          style={{ fontSize: "0.78rem", lineHeight: 1.35, textAlign: "right", color: TONS[resultado.tom] || AUTOPILOT_COLORS.text }}
        >
          {resultado.texto}
        </div>
      ) : null}
    </div>
  );
}
